import { formatPercentage, formatNumber } from '../components/utils';
import { parseAnalystRating } from '../utils/ratingScale';
import { rangePositionScore } from '../shared/earningsSetup';

export const INITIAL_SORT_CONFIG = { key: 'date', direction: 'ascending' };

export const DEFAULT_COLOR_SETTINGS = [
  {
    conditions: [
      { field: 'trafficChange', operator: '>', value: '0' },
      { field: 'stockChange', operator: '<=', value: '0' },
      { field: 'outlook', operator: '>', value: '0.7' }
    ],
    color: '#e6ffe6' // Light green
  },
  {
    conditions: [
      { field: 'trafficChange', operator: '<', value: '0' },
      { field: 'stockChange', operator: '>=', value: '0' }
    ],
    color: '#ffe6e6' // Light red
  },
  {
    conditions: [], // Empty conditions array will match all rows
    color: 'transparent' // Default background for rows that don't match any other rules
  }
];

// Define all possible columns with necessary properties
// Sort order for the Guidance column (issue #306): raised at one end, lowered at the other, and
// rows with no analysed call (-1) grouped together beyond both.
const GUIDANCE_RANK = { raised: 4, initiated: 3, maintained: 2, none: 1, lowered: 0 };
const guidanceRank = (row) => {
  const direction = row?.guidance?.direction;
  return Object.hasOwn(GUIDANCE_RANK, direction) ? GUIDANCE_RANK[direction] : -1;
};

export const ALL_COLUMNS = [
  // Fixed Columns
  {
    key: 'date',
    label: 'Date',
    tooltip: 'Next earnings date or economic event date',
    fields: ['date'],
    sortField: 'date',
    format: 'date',
    isFixed: true,
    isSortable: true,
    sortFunction: (a, b) => {
      // If both dates are null/undefined, maintain their relative position
      if (!a.date && !b.date) return 0;
      // If only a's date is null/undefined, it should go last
      if (!a.date) return 1;
      // If only b's date is null/undefined, it should go last
      if (!b.date) return -1;
      // Normal date comparison for valid dates
      return new Date(a.date) - new Date(b.date);
    }
  },
  {
    key: 'name',
    label: 'Company',
    tooltip: 'Company name and ticker symbol',
    fields: ['name', 'tickerSymbol'],
    sortField: 'name',
    format: null,
    isFixed: true,
    isSortable: true,
    isCompanyColumn: true,
  },
  {
    key: 'actions',
    label: '',
    isFixed: true,
    tooltip: 'Column settings',
    isHidden: true,
    fields: [],
    isActionsColumn: true,
    isSortable: false,  // Set isSortable to false
  },
  // Configurable Columns
  {
    key: 'stockPrice',
    label: 'Price',
    tooltip: 'Current stock price and today\'s change',
    fields: ['stockPrice', 'regularMarketChangePercent'],
    // Sort on today's MOVE, not the dollar price (issue #270) — an absolute
    // share price isn't comparable across rows. This also aligns the sort with
    // the filter: CustomFilterModal's detectDisplayChangeColumns() auto-detects
    // stockPrice -> regularMarketChangePercent from `fields`, and that branch
    // runs before the sortField branch, so filtering was already on the change %.
    // (It used to reach that result from a hardcoded seed; deleted in #272.)
    sortField: 'regularMarketChangePercent',
    format: 'currencyWithChange', // Custom format to handle price and percentage change
    isSortable: true,
  },
  {
    key: 'stockChange',
    // Drift from the close AFTER the last report, plus how many trading
    // sessions have passed since it (issue #229). `fields` no longer lists
    // stockPrice because the cell no longer renders it — the adjacent Price
    // column already does. Filtering is unaffected: CustomFilterModal's
    // multi-field branch already resolved this column to `sortField`, which is
    // the same 'stockChange' the single-field branch now picks.
    label: 'Since Earnings',
    tooltip: 'Price change since the close after the last earnings report, and how many trading sessions ago that was. Hover a cell for the report date and the move on the day.',
    fields: ['stockChange'],
    sortField: 'stockChange',
    format: null,
    isSortable: true,
  },
  {
    // Guidance direction from the newest earnings call we have analysed (issue #306). NOT a
    // screen_query field: StockTable merges it onto each row from the earnings-highlights worker,
    // and only while this column is displayed — so it cannot be a server-side filter.
    key: 'guidance',
    label: 'Guidance',
    tooltip: 'Whether management raised, maintained, lowered or initiated guidance on the latest earnings call we have analysed. Hover a cell for the detail and the call date.',
    fields: ['guidance'],
    sortField: 'guidance',
    format: null,
    isSortable: true,
    isFilterable: false,
    sortFunction: (a, b) => guidanceRank(b) - guidanceRank(a),
  },
  {
    key: 'priceChange3d',
    label: '3D Change',
    tooltip: 'Current price and % change over the last 3 trading days',
    fields: ['stockPrice', 'priceChange3d'],
    sortField: 'priceChange3d',
    format: null,
    isSortable: true,
  },
  {
    key: 'priceChange7d',
    label: '7D Change',
    tooltip: 'Current price and % change over the last 7 trading days',
    fields: ['stockPrice', 'priceChange7d'],
    sortField: 'priceChange7d',
    format: null,
    isSortable: true,
  },
  {
    key: 'priceChange14d',
    label: '14D Change',
    tooltip: 'Current price and % change over the last 14 trading days',
    fields: ['stockPrice', 'priceChange14d'],
    sortField: 'priceChange14d',
    format: null,
    isSortable: true,
  },
  {
    key: 'priceChange1m',
    label: '1M Change',
    tooltip: 'Current price and % change over the last month',
    fields: ['stockPrice', 'priceChange1m'],
    sortField: 'priceChange1m',
    format: null,
    isSortable: true,
  },
  {
    key: 'priceChange3m',
    label: '3M Change',
    tooltip: 'Current price and % change over the last 3 months',
    fields: ['stockPrice', 'priceChange3m'],
    sortField: 'priceChange3m',
    format: null,
    isSortable: true,
  },
  {
    key: 'trafficChange',
    label: 'Web Traffic',
    tooltip: 'Website traffic with year-over-year change',
    fields: ['trafficChange'],
    sortField: 'trafficChange',
    format: 'percentage',
    isSortable: true,
  },
  {
    key: 'positionsPercentage',
    label: 'Job Openings',
    tooltip: 'Open positions and % relative to total employees',
    fields: ['positionsPercentage'],
    sortField: 'positionsPercentage',
    format: 'percentage',
    isSortable: true,
  },
  {
    key: 'outlook',
    label: 'Employee Outlook',
    tooltip: "Employee sentiment about the company's future (with change since last update)",
    fields: ['outlook', 'outlookDelta'],
    sortField: 'outlook',
    isSortable: true,
  },
  {
    key: 'revenuePerEmployee',
    label: 'Rev/Employee',
    tooltip: 'Annual revenue per employee - measures workforce efficiency (derived from Market Cap / P/S ratio / employees)',
    fields: ['revenuePerEmployee'],
    sortField: 'revenuePerEmployee',
    format: (value) => {
      if (value == null || isNaN(value)) return null;
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}M`;
      return `$${Math.round(value)}K`;
    },
    isSortable: true,
  },
  {
    key: 'dividendYield',
    label: 'Dividend Yield',
    tooltip: 'Annual dividend yield',
    fields: ['dividendYield'],
    sortField: 'dividendYield',
    isSortable: true,
  },
  {
    key: 'marketCap',
    label: 'Market Cap',
    tooltip: 'Total market capitalization',
    fields: ['marketCap'],
    sortField: 'marketCap',
    format: 'currency',
    isSortable: true,
  },
  // Action Column (for row expand handler)

  {
    key: 'pegRatio',
    label: 'PEG Ratio',
    tooltip: 'Price/Earnings to Growth ratio - lower values suggest better value',
    fields: ['pegRatio'],
    sortField: 'pegRatio',
    format: (value) => formatNumber(value, 2),
    isSortable: true,
  },
  {
    key: 'growth',
    label: 'Growth',
    tooltip: 'Revenue growth rate',
    fields: ['revenueGrowth'],
    sortField: 'revenueGrowth',
    format: (value) => {
        console.log('growth format input:', value);
        return formatPercentage(value);
    },
    isSortable: true,
  },
  {
    key: 'shortFloat',
    label: 'Short %',
    tooltip: 'Percentage of float shares sold short',
    fields: ['shortPercentOfFloat'],
    sortField: 'shortPercentOfFloat',
    format: (value) => {
        console.log('shortFloat format input:', value);
        return formatPercentage(value);
    },
    isSortable: true,
  },
  {
    key: 'margins',
    label: 'Margins',
    tooltip: 'Gross margin with operating margin change',
    fields: ['grossMargins', 'operatingMargins'],
    sortField: 'grossMargins',
    format: null,
    isSortable: true,
  },
  {
    key: 'beta',
    label: 'Beta',
    tooltip: 'Stock volatility relative to the market',
    fields: ['beta'],
    sortField: 'beta',
    format: (value) => formatNumber(value, 2),
    isSortable: true,
  },
  {
    key: 'alpha_12m',
    label: 'Alpha',
    tooltip: 'Jensen\'s Alpha (12 months) - excess return vs market adjusted for beta risk. Positive = outperformed SPY',
    fields: ['alpha_12m'],
    sortField: 'alpha_12m',
    format: (value) => formatPercentage(value),
    isSortable: true,
  },
  {
    key: 'technicalSignals',
    label: 'Signals',
    tooltip: 'Technical analysis signals',
    fields: ['technicalSignals'],
    sortField: 'latestSignal.strength',
    isSortable: true,
    sortFunction: (a, b) => {
      // First, check for buy signals
      const aHasBuy = a.technicalSignals?.some(s => s.type.toUpperCase() === 'BUY');
      const bHasBuy = b.technicalSignals?.some(s => s.type.toUpperCase() === 'BUY');
      
      if (aHasBuy !== bHasBuy) return bHasBuy ? 1 : -1;
      
      // If both have same signal types, compare by highest strength
      const aStrength = Math.max(...(a.technicalSignals?.map(s => s.strength) || [0]));
      const bStrength = Math.max(...(b.technicalSignals?.map(s => s.strength) || [0]));
      
      return bStrength - aStrength;
    }
  },
  {
    key: 'extendedHours',
    label: 'Extended Hours Price',
    tooltip: 'Pre-market or Post-market price and percentage change',
    fields: ['preMarketPrice', 'preMarketChangePercent', 'postMarketPrice', 'postMarketChangePercent'],
    sortField: 'extendedHoursChangePercent',
    isSortable: true,
    sortFunction: (a, b) => {
      const getChangePercent = (stock) => {
        return stock.preMarketChangePercent || stock.postMarketChangePercent || 0;
      };
      
      return getChangePercent(b) - getChangePercent(a);
    },
    format: (row) => {
      // If we have pre-market data, use it
      if (row.preMarketPrice) {
        return {
          price: row.preMarketPrice,
          change: row.preMarketChangePercent || 0,
          type: 'pre'
        };
      }
      // Otherwise if we have post-market data, use it
      if (row.postMarketPrice) {
        return {
          price: row.postMarketPrice,
          change: row.postMarketChangePercent || 0,
          type: 'post'
        };
      }
      // No extended hours data available
      return null;
    }
  },
  {
    key: 'industry',
    label: 'Industry',
    tooltip: 'Company industry classification',
    fields: ['industry'],
    sortField: 'industry',
    isSortable: true,
    format: null,
  },
  {
    key: 'analystRating',
    label: 'Analysts Rating',
    tooltip: 'Analyst consensus: 1=Strong Buy, 2=Buy, 3=Hold, 4=Underperform, 5=Sell',
    fields: ['averageAnalystRating'],
    sortField: 'averageAnalystRating',
    isSortable: true,
    // "2.0 - Buy" -> 2; no (or unparseable) rating sorts last.
    sortFunction: (a, b) =>
      (parseAnalystRating(a.averageAnalystRating)?.value ?? 999)
      - (parseAnalystRating(b.averageAnalystRating)?.value ?? 999),
  },
  {
    key: 'fiftyTwoWeekRange',
    label: '52W Range',
    tooltip: 'Position within 52-week range (0% = at low, 100% = at high)',
    fields: ['stockPrice', 'fiftyTwoWeekLow', 'fiftyTwoWeekHigh'],
    sortField: 'fiftyTwoWeekRangePercent',
    isSortable: true,
    // One definition of "where in its range" (rangePositionScore, clamped 0–100),
    // shared with the cell and the stock profile; no range sorts first (-1).
    sortFunction: (a, b) =>
      (rangePositionScore(a.stockPrice, a.fiftyTwoWeekLow, a.fiftyTwoWeekHigh) ?? -1)
      - (rangePositionScore(b.stockPrice, b.fiftyTwoWeekLow, b.fiftyTwoWeekHigh) ?? -1),
  },
  {
    key: 'expectationsBar',
    label: 'Expectations Bar',
    tooltip:
      'How much good news is already priced in going into earnings. 0 = washed out (little priced in), 100 = crowded (a lot to live up to). ' +
      'Blends 52-week range position, recent run-up, sector sentiment, options positioning and market fear & greed, using whichever are available. ' +
      'Weights are calibrated against ~3,400 actual earnings reactions (Apr–Aug 2026): historically, high-bar stocks reacted smaller and slightly weaker, ' +
      'washed-out stocks bigger and slightly stronger. Descriptive, not directional — it does not predict which way the stock will move.',
    fields: ['expectationsBar', 'expectationsBarRating', 'expectationsBarCoverage', 'impliedMovePct'],
    sortField: 'expectationsBar',
    isSortable: true,
    // Nulls sort last ascending, matching the `date` column's convention.
    // Ascending is the useful direction here: it surfaces washed-out names.
    sortFunction: (a, b) => {
      if (a.expectationsBar == null && b.expectationsBar == null) return 0;
      if (a.expectationsBar == null) return 1;
      if (b.expectationsBar == null) return -1;
      return a.expectationsBar - b.expectationsBar;
    },
  },
  {
    key: 'notes',
    label: 'Notes',
    tooltip: 'Personal notes about this stock (only visible in watchlist)',
    fields: ['notes'],
    sortField: 'notes',
    isSortable: true,
    isEditable: true,
    format: null,
    watchlistOnly: true, // Flag to indicate this column is only for watchlist
  },
];

// One column by key — for surfaces outside the table (the stock profile) that
// show the same field and must reuse the column's own tooltip and formatter.
export const columnByKey = (key) => ALL_COLUMNS.find((column) => column.key === key);
export const columnTooltip = (key) => columnByKey(key)?.tooltip;

// Define default columns to be displayed
export const DEFAULT_COLUMNS = [
  columnByKey('date'),
  columnByKey('name'),
  columnByKey('outlook'),
  columnByKey('marketCap'),
  columnByKey('growth'),
  columnByKey('shortFloat'),
  columnByKey('technicalSignals'),
  columnByKey('analystRating'),
  columnByKey('industry'),
  // Remove notes from default columns as we'll add it dynamically in the watchlist tab
  // Exclude the actions column here
];

// Portfolio view columns
export const PORTFOLIO_COLUMNS = [
  columnByKey('name'),
  { key: 'shares', label: 'Shares', tooltip: 'Number of shares held', fields: ['shares'], sortField: 'shares', format: 'number', isSortable: true },
  { key: 'avgCost', label: 'Avg Cost', tooltip: 'Average cost basis per share', fields: ['avgCost'], sortField: 'avgCost', format: 'currency', isSortable: true },
  columnByKey('stockPrice'),
  { key: 'marketValue', label: 'Value', tooltip: 'Current market value of position', fields: ['marketValue'], sortField: 'marketValue', format: 'currency', isSortable: true },
  { key: 'plDollar', label: 'P&L $', tooltip: 'Unrealized profit/loss in dollars', fields: ['plDollar'], sortField: 'plDollar', format: 'currency', isSortable: true },
  { key: 'plPercent', label: 'P&L %', tooltip: 'Unrealized profit/loss percentage', fields: ['plPercent'], sortField: 'plPercent', format: 'percentage', isSortable: true },
  { key: 'weight', label: 'Weight', tooltip: 'Position weight in portfolio', fields: ['weight'], sortField: 'weight', format: 'percentage', isSortable: true },
  columnByKey('industry'),
].filter(Boolean);

// Optionally, export FIELD_NAMES if used elsewhere
export const FIELD_NAMES = ALL_COLUMNS.reduce((fields, column) => {
  column.fields.forEach((field) => {
    if (!fields.includes(field)) {
      fields.push(field);
    }
  });
  return fields;
}, []);

